import type { SliderFontSchemaType, SliderSchemaType } from '@vedalib/editor/lib/brand'
import { ProgressType } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import { useContext } from 'use-context-selector'

import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { useProjectT } from 'services/ProjectLabels'
import { DeviceMode } from 'services/Store/Project/enums'
import { testProps } from 'utils/test/qaData'

import SliderContext from '../SliderContext'
import { ArrowPosition } from '../sliderTypes'
import DotStyled from '../styled/DotStyled'
import ListItemStyled from '../styled/ListItemStyled'
import * as s from './Progress.module.scss'
import { getMargins } from './getMargins'

interface IDotProps {
  number: number
  styles: ElementStyleCss<SliderSchemaType>
  handleClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  focused: boolean
}

interface IDotsProps {
  type?: ProgressType
  includeSlide?: {
    first: boolean
    last: boolean
  }
  styles: ElementStyleCss<SliderSchemaType>
  font?: ElementFontCss<SliderFontSchemaType>
}

const Dot = ({ number, styles, handleClick, focused }: IDotProps) => {
  const { slideIndex } = useContext(SliderContext)
  const isActive = slideIndex === number
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isActive && focused) {
      ref.current?.focus()
    }
  }, [isActive, focused])

  return (
    <ListItemStyled $styles={styles} role='listitem'>
      <DotStyled
        {...testProps({ el: 'sliderElDot', index: number, isActive })}
        $selected={isActive}
        $styles={styles}
        aria-current={isActive ? 'true' : 'false'}
        aria-label={`Go to slide ${number + 1}`}
        onClick={handleClick}
        ref={ref}
        tabIndex={isActive ? 0 : -1}
      />
    </ListItemStyled>
  )
}

const Progress = ({ type, includeSlide, styles, font }: IDotsProps) => {
  const pt = useProjectT()
  const dotsRef = useRef<HTMLDivElement>(null)
  const { items, slideIndex, mode, goToSlide, nextSlide, prevSlide } = useContext(SliderContext)
  const [focused, setFocused] = useState<boolean>(false)
  const isMobile = mode.deviceMode === DeviceMode.mobile
  const margins = getMargins(String(styles.indents.margin))
  const isSlidePosition = styles.icons.sliderPosition === ArrowPosition.slide
  const numbering =
    items.length - Object.values(includeSlide || {}).reduce((res, val) => +!val + res, 0)

  let label
  if (includeSlide && !includeSlide?.first && slideIndex === 0) {
    label = pt('elements.slider.start')
  } else if (!includeSlide?.last && slideIndex + 1 === items.length) {
    label = pt('elements.slider.end')
  } else {
    label = `${!includeSlide?.first ? slideIndex : slideIndex + 1}/${numbering}`
  }

  const handleDotClick = (number: number) => {
    goToSlide(number)
  }

  const handleKeydown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      nextSlide()
    }
    if (e.key === 'ArrowLeft') {
      prevSlide()
    }
    if (e.key === 'Home') {
      goToSlide(0)
    }
    if (e.key === 'End') {
      goToSlide(items.length - 1)
    }
  }

  const padding =
    isMobile && isSlidePosition ? 4 : styles.icons.sliderPosition === ArrowPosition.slide ? 8 : 0
  const dotsCn = cn(s.dots, s.center)

  return (
    <div className={s.root}>
      <div
        className={dotsCn}
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        onKeyDown={handleKeydown}
        ref={dotsRef}
        style={{
          paddingBottom: padding,
          marginTop: (isMobile && isSlidePosition) || isSlidePosition ? margins.bottom : undefined,
        }}
      >
        {type === ProgressType.number ? (
          <div className={s.numbered} style={font?.base}>
            {label}
          </div>
        ) : (
          <ul className={s.list} role='list'>
            {items.map((_item, i) => (
              <Dot
                focused={focused}
                handleClick={() => handleDotClick(i)}
                key={i}
                number={i}
                styles={styles}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default Progress
